"use client";

import React, { useState, useEffect } from "react";
import styles from "./PerspectiveCarousel.module.css";
import VideoPlayer from "../VideoPlayer/VideoPlayer";

const perspectives = ["front", "side", "rear", "45_degree"];

const perspectiveLabels = {
  front: "FRONT",
  side: "SIDE",
  rear: "REAR",
  "45_degree": "45°",
};

const PerspectiveCarousel = ({
  content,
  preferredPerspective = null,
  onPerspectiveChange,
  videoMapping: providedVideoMapping = null,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [internalVideoMapping, setInternalVideoMapping] = useState({});
  const videoMapping = providedVideoMapping ?? internalVideoMapping;

  useEffect(() => {
    setCurrentIndex(0);
    setInternalVideoMapping({});
  }, [content?.content_id]);

  useEffect(() => {
    if (!content?.content_id || providedVideoMapping !== null) return;

    perspectives.forEach((perspective) => {
      if (internalVideoMapping[perspective] === undefined) {
        fetch(`/api/content/${content.content_id}/perspectives?perspective=${perspective}`)
          .then((res) => res.json())
          .then((data) => {
            let selectedVideo = null;

            if (data.videos && data.videos.length > 0) {
              const mainVideo = data.videos.find((v) => v.is_main);
              selectedVideo = mainVideo
                ? mainVideo.video_path
                : data.videos[0].video_path;
            }

            setInternalVideoMapping((prev) => ({
              ...prev,
              [perspective]: selectedVideo,
            }));
          })
          .catch((err) => {
            console.error(`Error fetching videos for ${perspective}:`, err);
            setInternalVideoMapping((prev) => ({ ...prev, [perspective]: null }));
          });
      }
    });
  }, [content?.content_id, internalVideoMapping, providedVideoMapping]);

  const availablePerspectives = perspectives.filter(
    (p) => videoMapping[p] !== undefined && videoMapping[p] !== null
  );

  useEffect(() => {
    if (availablePerspectives.length === 0) return;

    if (
      preferredPerspective &&
      availablePerspectives.includes(preferredPerspective)
    ) {
      const preferredIndex = perspectives.indexOf(preferredPerspective);

      if (preferredIndex !== currentIndex) {
        setCurrentIndex(preferredIndex);
      }

      return;
    }

    const currentPerspective = perspectives[currentIndex];

    if (!availablePerspectives.includes(currentPerspective)) {
      const firstAvailable = availablePerspectives[0];
      const firstAvailableIndex = perspectives.indexOf(firstAvailable);

      setCurrentIndex(firstAvailableIndex);

      if (onPerspectiveChange) {
        onPerspectiveChange(firstAvailable);
      }
    }
  }, [
    preferredPerspective,
    availablePerspectives,
    currentIndex,
    onPerspectiveChange,
  ]);

  const currentPerspective = perspectives[currentIndex];
  const currentVideo = videoMapping[currentPerspective];

  const notifyPerspectiveChange = (nextPerspective) => {
    if (onPerspectiveChange) {
      onPerspectiveChange(nextPerspective);
    }
  };

  const handleNext = () => {
    if (availablePerspectives.length < 2) return;

    setCurrentIndex((prevIndex) => {
      const currentPerspectiveFromIndex = perspectives[prevIndex];
      const availableIndex = availablePerspectives.indexOf(currentPerspectiveFromIndex);
      const nextAvailableIndex = (availableIndex + 1) % availablePerspectives.length;
      const nextPerspective = availablePerspectives[nextAvailableIndex];

      notifyPerspectiveChange(nextPerspective);

      return perspectives.indexOf(nextPerspective);
    });
  };

  const handlePrev = () => {
    if (availablePerspectives.length < 2) return;

    setCurrentIndex((prevIndex) => {
      const currentPerspectiveFromIndex = perspectives[prevIndex];
      const availableIndex = availablePerspectives.indexOf(currentPerspectiveFromIndex);
      const previousAvailableIndex =
        (availableIndex - 1 + availablePerspectives.length) % availablePerspectives.length;
      const previousPerspective = availablePerspectives[previousAvailableIndex];

      notifyPerspectiveChange(previousPerspective);

      return perspectives.indexOf(previousPerspective);
    });
  };

  const displayLabel = perspectiveLabels[currentPerspective]
    ? perspectiveLabels[currentPerspective]
    : currentPerspective.toUpperCase();

  return (
    <div className={styles.carouselContainer}>
      <div className={styles.videoWrapper}>
        {currentVideo ? (
          <VideoPlayer
            key={`${content?.content_id}-${currentPerspective}-${currentVideo}`}
            src={currentVideo}
            playing={true}
            loop={true}
            muted={true}
          />
        ) : (
          <div className={styles.noVideo}>
            <p>No video available for {currentPerspective} view.</p>
          </div>
        )}

        <div className={styles.overlayControls}>
          {availablePerspectives.length > 1 ? (
            <>
              <button onClick={handlePrev} className={styles.controlButton}>
                Previous
              </button>

              <span className={styles.label}>
                {displayLabel} VIEW
              </span>

              <button onClick={handleNext} className={styles.controlButton}>
                Next
              </button>
            </>
          ) : (
            <span className={styles.label}>
              {displayLabel} VIEW
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerspectiveCarousel;
